import dayjs from 'dayjs';
import {
  CompanyShareSelection,
  IChildCompany,
  IShareTarget,
} from '../../../dto/MessageSharing.dto';

const DATE_ONLY_FORMAT = 'YYYY-MM-DD';

export const toDateTimeLocalValue = (
  value?: dayjs.ConfigType | null,
): string => {
  if (!value) {
    return '';
  }

  return dayjs(value).format(DATE_ONLY_FORMAT);
};

export const getDefaultShareWindowDraft = (): Pick<
  CompanyShareSelection,
  'startsAt' | 'endsAt'
> => {
  const currentDate = dayjs();

  return {
    startsAt: toDateTimeLocalValue(currentDate),
    endsAt: toDateTimeLocalValue(currentDate),
  };
};

export const buildShareTargets = (
  selections: CompanyShareSelection[],
): IShareTarget[] => {
  const targets: IShareTarget[] = [];

  selections.forEach(selection => {
    if (!selection.startsAt || !selection.endsAt) {
      return;
    }

    const normalizedWindow = {
      starts_at: dayjs(selection.startsAt).startOf('day').toISOString(),
      ends_at: dayjs(selection.endsAt).endOf('day').toISOString(),
    };

    if (selection.wholeCompany) {
      targets.push({
        target_type: 'company',
        target_company_id: selection.companyId,
        ...normalizedWindow,
      });

      return;
    }

    Array.from(new Set(selection.selectedStationIds)).forEach(stationId => {
      targets.push({
        target_type: 'station',
        target_company_id: selection.companyId,
        target_station_id: stationId,
        ...normalizedWindow,
      });
    });

    Array.from(new Set(selection.selectedSchoolIds)).forEach(schoolId => {
      targets.push({
        target_type: 'company_school',
        target_company_id: selection.companyId,
        target_company_school_id: schoolId,
        ...normalizedWindow,
      });
    });
  });

  return targets;
};

export const hydrateShareSelections = (
  targets: IShareTarget[],
  childCompanies: IChildCompany[] = [],
): CompanyShareSelection[] => {
  const selectionMap = new Map<number, CompanyShareSelection>();

  targets.forEach(target => {
    const existingSelection = selectionMap.get(target.target_company_id);
    const companyName =
      childCompanies.find(company => company.id === target.target_company_id)
        ?.name ??
      existingSelection?.companyName ??
      '';

    const nextSelection: CompanyShareSelection = existingSelection ?? {
      companyId: target.target_company_id,
      companyName,
      wholeCompany: false,
      selectedStationIds: [],
      selectedSchoolIds: [],
      startsAt: toDateTimeLocalValue(target.starts_at),
      endsAt: toDateTimeLocalValue(target.ends_at),
    };

    nextSelection.companyName = companyName;

    if (!nextSelection.startsAt) {
      nextSelection.startsAt = toDateTimeLocalValue(target.starts_at);
    }

    if (!nextSelection.endsAt) {
      nextSelection.endsAt = toDateTimeLocalValue(target.ends_at);
    }

    if (target.target_type === 'company') {
      nextSelection.wholeCompany = true;
    }

    if (target.target_type === 'station' && target.target_station_id) {
      nextSelection.selectedStationIds = Array.from(
        new Set([
          ...nextSelection.selectedStationIds,
          target.target_station_id,
        ]),
      );
    }

    if (
      target.target_type === 'company_school' &&
      target.target_company_school_id
    ) {
      nextSelection.selectedSchoolIds = Array.from(
        new Set([
          ...nextSelection.selectedSchoolIds,
          target.target_company_school_id,
        ]),
      );
    }

    selectionMap.set(target.target_company_id, nextSelection);
  });

  return Array.from(selectionMap.values()).sort((left, right) => {
    const leftName = left.companyName || `${left.companyId}`;
    const rightName = right.companyName || `${right.companyId}`;

    return leftName.localeCompare(rightName);
  });
};
